import { useSearchParams } from 'react-router-dom';
import { useViewMode } from '@/hooks/useViewMode';

export type ViewMode = 'auto' | 'totem' | 'mobile';

interface ViewModeSwitchProps {
  /**
   * Optional CSS classes to customize the container
   */
  className?: string;
}

/**
 * Switch component to toggle between totem, mobile, and auto view modes
 *
 * Updates the URL parameter (?view=totem|mobile) and applies
 * data-mode="totem" attribute to <html> element
 *
 * Usage:
 * <ViewModeSwitch />
 * <ViewModeSwitch className="fixed top-4 right-4 z-50" />
 */
export function ViewModeSwitch({ className = '' }: ViewModeSwitchProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isTotemMode } = useViewMode();

  const currentMode = (searchParams.get('view') || 'auto') as ViewMode;

  const handleModeChange = (mode: ViewMode) => {
    if (mode === 'auto') {
      searchParams.delete('view');
    } else {
      searchParams.set('view', mode);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className={`flex gap-2 p-2 bg-surface rounded-lg border border-border ${className}`}>
      {/* Auto Button */}
      <button
        onClick={() => handleModeChange('auto')}
        className={`
          px-3 py-1 rounded-md text-label-sm font-medium transition-colors
          ${
            currentMode === 'auto'
              ? 'bg-primary text-accent'
              : 'bg-surface-light text-text-primary hover:bg-border'
          }
          focus-visible:outline-2 focus-visible:outline-primary
        `}
        title="Auto-detect based on viewport (1080px breakpoint)"
      >
        Auto / Mobile
      </button>


      {/* Totem Button */}
      <button
        onClick={() => handleModeChange('totem')}
        className={`
          px-3 py-1 rounded-md text-label-sm font-medium transition-colors
          ${
            currentMode === 'totem'
              ? 'bg-primary text-accent'
              : 'bg-surface-light text-text-primary hover:bg-border'
          }
          focus-visible:outline-2 focus-visible:outline-primary
        `}
        title="Force totem layout (32px spacing, 1.4x typography)"
      >
        Totem
      </button>

      {/* Status Indicator */}
      <div className="flex items-center gap-1 pl-2 border-l border-border text-text-primary text-label-sm">
        <span className="w-2 h-2 rounded-full bg-primary" />
        {isTotemMode ? 'Totem View' : 'Mobile View'}
      </div>
    </div>
  );
}
